import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { RemixiconComponentType } from "@remixicon/react";
import { cn } from "~/lib/utils";

const iconButtonVariants = cva(
  "group focus-visible:ring-ring inline-flex w-fit cursor-pointer items-center justify-center gap-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors focus-visible:ring-1 focus-visible:outline-none disabled:pointer-events-none disabled:cursor-default",
  {
    variants: {
      variant: {
        primary:   "text-bouncie-500 disabled:text-disabled-text border border-white bg-white hover:border-[#d9e4f7] hover:bg-[#d9e4f7] disabled:border-transparent disabled:bg-transparent",
        secondary: "disabled:text-disabled-text border border-[#0a275c] bg-transparent text-[#0a275c] hover:bg-[#d9e4f7] disabled:border-transparent",
        tertiary:  "disabled:text-disabled-text bg-transparent text-[#0a275c] hover:bg-[#d9e4f7]",
        glass:     "disabled:text-disabled-text bg-gradient-to-br from-white/20 to-white/10 text-white shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.3),inset_1px_1px_0_rgba(255,255,255,0.55),2px_4px_12px_rgba(0,0,0,0.25)] backdrop-blur-xl backdrop-saturate-200 hover:from-white/30 hover:to-white/20",
        ghost: "hover:text-bouncie-500 bg-transparent text-white hover:bg-white",
      },
      size: {
        default: "h-12 w-12", // 48px — Figma L (Default)
        sm: "h-8 w-8",        // 32px — Figma S
        lg: "h-14 w-14",      // 56px — no Figma equivalent, kept for compat
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export type IconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> &
  VariantProps<typeof iconButtonVariants> & {
    icon: RemixiconComponentType;
    hoverIcon?: RemixiconComponentType;
    iconClassName?: string;
    asChild?: boolean;
    children?: React.ReactNode;
  };

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, icon: Icon, hoverIcon: HoverIcon, iconClassName, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const iconSize = size === "sm" ? "h-3 w-3" : "h-4 w-4"; // 12px S, 16px L
    return (
      <Comp className={cn(iconButtonVariants({ variant, size, className }))} ref={ref} {...props}>
        <Slottable>{children}</Slottable>
        {HoverIcon ? (
          <>
            <Icon className={cn(iconSize, iconClassName, "group-hover:hidden")} />
            <HoverIcon className={cn(iconSize, iconClassName, "hidden group-hover:block")} />
          </>
        ) : (
          <Icon className={cn(iconSize, iconClassName)} />
        )}
      </Comp>
    );
  },
);
IconButton.displayName = "IconButton";

export { IconButton, iconButtonVariants };
